/**
 * poFxToEur — the EUR total of a purchase order, for lists and sorting.
 *
 * @description
 *   Holds the FX direction for PO money shown outside PODetail. The house
 *   convention for `purchase_orders.conversion_rate` is **EUR per one unit of PO
 *   currency**, i.e. `EUR = native × rate` (a USD PO carries ≈ 0.85). Pinned by
 *   `backend/po_cost/fx.py::normalize_conversion_rate` and
 *   `docs/knowledge/operations/data-quirks.md` §"PO `conversion_rate` canonical
 *   direction = EUR-per-native".
 *
 *   This module exists because the logic lived inline in `POOverview.jsx`, where
 *   nothing could unit-test it: importing that component pulls `App.js` through
 *   `SidebarAndHeaderBar`, and the circular import kills the Jest suite before a
 *   single assertion runs. It dividing by the rate for thirteen months was the
 *   direct consequence (finding D8, 2026-09-07).
 *
 * @useWhen
 *   - Rendering or sorting a PO money figure in EUR outside PODetail
 *   - Any list or report that mixes EUR and non-EUR purchase orders
 *
 * @dontUseFor
 *   - Writing a rate back to the server — it normalizes on write
 *     (`normalize_conversion_rate`, routes.py); do not pre-correct a payload
 *   - Non-PO rate columns: `rma_vendor_credits.fx_rate` and `backend/rma/credit_pricing.py`
 *     use the OPPOSITE convention ("1 EUR = X native"). This detector would mangle them
 *
 * @related
 *   - backend/po_cost/fx.py — the authority this file mirrors; keep in step
 *   - frontend/src/PODetail.jsx — multiplies the RAW stored rate with no detector,
 *     so on a legacy inverted PO the detail view is still ~35 % high. Filed to
 *     docs/backlog-auto-repair/2026-09-07-podetail-und-fracht-lesen-den-rohen-kurs.md
 */

/**
 * Normalize a stored PO conversion rate to the canonical EUR-per-native direction.
 *
 * Mirrors `backend/po_cost/fx.py::normalize_conversion_rate`, with ONE deliberate
 * difference: the backend raises (→ HTTP 422) on an unusable rate. A table cell
 * cannot raise, so this returns `null` and lets the caller decide.
 *
 * 🔴 The inversion branch is not defensive programming, it is measured. Rows
 * written before the 2026-07-11 repair may still hold the inverted direction and
 * were never backfilled. Prod, 2026-09-06, `trashed = false`: 78 non-EUR POs —
 * 43 canonical `(0,1)`, **17 inverted `>= 1.01`**, 18 unusable (`= 1.0`).
 * Multiplying an inverted row by its stored rate overstates it by ~35 %.
 *
 * 🔴 Since 2026-09-07 both files also carry a currency guard: outside USD/EUR the
 * detector cannot read a direction at all, so the backend raises and this port
 * returns `null` (→ the caller renders the native amount). Owner decision O29.
 *
 * ⚠️ One documented divergence beyond the `null`: `fx.py` quantizes the inverted
 * branch to six decimal places (`.quantize(Decimal("0.000001"))`), this port does
 * not. Invisible at the two decimals anything renders, but a file whose whole
 * premise is "stay in step with `fx.py`" has to say where it does not.
 *
 * @param {string} currency  PO currency, e.g. 'USD'. EUR is identity.
 * @param {*}      rate      the stored `conversion_rate` (number or numeric string)
 * @returns {number|null}    EUR-per-native factor, or null when unusable
 */
// Currencies for which the SIZE of a rate still carries its direction, i.e. the
// canonical rate stays below 1.01. Mirrors `_HEURISTIK_ANWENDBAR` in fx.py.
// 🔴 Deliberately a list, not derived from the vendor's currency: a PO can carry
// currencies the vendor form no longer offers (PODetail offers GBP, CHF, JPY,
// CNY), so a derived set would widen itself the moment such a PO appears —
// exactly when the block would need to bite. (The vendor form did, until 2026-09-25.)
const HEURISTIC_APPLIES = new Set(['USD']);

/**
 * Kann der Detektor fuer diese Waehrung ueberhaupt eine Richtung lesen?
 *
 * 🔴 R16 C1 (Schlussrunde, 2026-11-02): `normalizeConversionRate` gibt fuer
 * jede Waehrung ausserhalb `{EUR, USD}` IMMER `null` zurueck. Wer daraus
 * „Kurs fehlt, bitte eintragen" ableitet, schickt den Bediener in eine
 * Handlung, die nicht gelingen kann (der Server antwortet 422, O29). Diese
 * eine Bedingung teilen sich Kopfsatz und Zeilen-Caption in `POStatusBar`.
 *
 * @param {string} currency  PO currency, e.g. 'GBP'
 * @returns {boolean}  true, wenn ein eingetragener Kurs lesbar sein KANN
 */
export function conversionRateReadable(currency) {
  const cur = String(currency || 'EUR').toUpperCase();
  return cur === 'EUR' || HEURISTIC_APPLIES.has(cur);
}

export function normalizeConversionRate(currency, rate) {
  const cur = String(currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return 1;

  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) return null;
  // Same unusable set as the backend: a foreign PO whose rate is exactly 1.0
  // carries no conversion information at all.
  if (r === 1) return null;

  // 🔴 Outside the set BOTH branches are unusable, not just the inversion.
  // For a currency stronger than the euro the inequality flips and both input
  // directions collapse onto the same wrong number — measured 2026-09-07 against
  // fx.py: GBP canonical 1.1642 AND inverted 0.858959 both yield 0.858959, i.e.
  // 26.22 % low. Blocking only the inversion would reject the RIGHT value and
  // silently keep the wrong one. Returning null makes the caller render the
  // native amount, which is the honest display for a rate we cannot read.
  if (!HEURISTIC_APPLIES.has(cur)) return null;

  // >= 1.01 can only be native-per-EUR — no genuine EUR-per-native rate for a
  // currency POG trades in sits that far above parity.
  if (r >= 1.01) return 1 / r;
  return r;
}

/**
 * EUR total for one PO row — the number the overview list shows AND sorts on.
 *
 * Prefers the server-canonical `po.total` (PO currency, from
 * `backend/purchase_orders/service.py::calculate_summaries`) and only recomputes
 * from line items when it is absent. The list serializer emits
 * `float(po.total or 0)`, so in production the recompute branch is unreachable;
 * it is kept because nothing guarantees that contract in writing.
 *
 * 🔴 Unusable rates keep TODAY's behaviour on purpose (factor 1 — the native
 * figure is shown unconverted). 18 prod POs are in that state, and repairing
 * them is R10 of the "Einkauf, Kosten, Logistik" plan: a data question with a
 * dry run and a four-eyes review. Turning them into a dash here would smuggle
 * that decision in through a rendering change.
 *
 * @param {object} po  a PO row from `GET /api/purchase_orders`
 * @returns {number|null} EUR total, or null when there is no PO
 */
/**
 * Was this PO's total actually converted, or is the number still native?
 *
 * 🔴 This exists because `euroTotalForPO` used to THROW THIS AWAY. Three lines
 * after `normalizeConversionRate` isolated exactly the rows that carry no usable
 * rate, the `factor === null ? 1 : factor` fold erased the distinction and the
 * caller received a bare number. The Rule 16 gate blocked on it, and the
 * argument is worth keeping: taking a column where all 78 non-EUR rows were
 * wrong, fixing 60 and leaving 18 SILENTLY wrong is more dangerous than the
 * uniformly unreliable column it replaces — because the accuracy the other 60
 * just earned is what makes the unmarked 18 expensive.
 *
 * The numeric value stays identical and so does the sort key — the RENDERED
 * STRING does not: `Intl.NumberFormat` groups thousands, so `1000.00` reads as
 * `$1,000.00`. Only the caller's knowledge changes, and what it does with it.
 * Repairing the underlying rows is still R10 and is NOT pre-empted by
 * labelling them.
 *
 * @param {object} po  a PO row from `GET /api/purchase_orders`
 * @returns {boolean} true when the figure is the PO-currency amount, unconverted
 */
export function isUnconvertedNative(po) {
  if (!po) return false;
  const cur = String(po.currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return false;
  return normalizeConversionRate(cur, po.conversion_rate) === null;
}

export function euroTotalForPO(po) {
  if (!po) return null;

  // 1) Use server total if present (PO-currency), else recompute (PO-currency)
  const items = Array.isArray(po.order_items) ? po.order_items : [];
  const subtotalTax = items.reduce((s, i) => {
    const qty  = Number(i.quantity) || 0;
    const cost = Number(i.cost_per_item) || 0;
    const tax  = Number(i.tax_pct) || 0;
    return s + qty * cost * (1 + tax / 100);
  }, 0);
  const feeSum = (po.additional_fees || []).reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const shippingOverride =
    po.shipping_invoice != null && po.shipping_invoice !== ''
      ? Number(po.shipping_invoice) || 0
      : Number(po.shipping_cost) || 0;

  const totalPO =
    Number.isFinite(Number(po.total))
      ? Number(po.total) // ← server canonical total (PO-currency)
      : subtotalTax + feeSum + shippingOverride;

  // 2) EUR conversion: EUR = native × rate (see normalizeConversionRate above).
  const cur = String(po.currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return totalPO;

  const factor = normalizeConversionRate(cur, po.conversion_rate);
  return totalPO * (factor === null ? 1 : factor);
}
